import React, { useState } from 'react';
import { Zap, Key, Plug } from 'lucide-react';
import APIView from './APIView';
import IntegrationDashboard from '../integrations/IntegrationDashboard';
import { isDevMode } from 'lib/featureFlags';
import { Tabs, TabPanel, type TabItem } from 'components/ui/tabs';
import { INTEGRATIONS_ENABLED } from 'core/features/integrations/config';
import { IntegrationsCatalog } from 'new-components/pages/integrations/IntegrationsCatalog';

/* ══════════════════════════════════════════════════════════════════════════
   Integrations / API

   Changes:
   • The tab strip was two plain buttons — no `role="tab"`, no roving
     tabindex, no arrow-key movement, and a `border-b` with no colour class
     (so the rule under the tabs was invisible). It now uses the shared `Tabs`,
     which brings the keyboard model and the correct ARIA wiring with it.
   • The tab panels are labelled by their tab, so a screen reader announces
     which section it entered.
   ══════════════════════════════════════════════════════════════════════════ */

type TabId = 'apps' | 'integrations' | 'api';

/* Merko Integrations handoff: the new 19-app catalogue is shown only when this
   environment's backend routes exist (REACT_APP_INTEGRATIONS_ENABLED). With the
   flag off this screen is exactly what it was before — the existing tab stays
   intact. With it on, the catalogue becomes the first tab and the existing
   dashboard stays beside it as "Native connections": native integrations are a
   separate system that must not be overwritten or migrated, so they keep their
   own screen rather than being folded into the catalogue. */
const TAB_ITEMS: TabItem<TabId>[] = [
    ...(INTEGRATIONS_ENABLED ? [{ value: 'apps' as const, label: 'Apps', icon: Plug }] : []),
    {
        value: 'integrations',
        label: INTEGRATIONS_ENABLED ? 'Native connections' : 'Integrations',
        icon: Zap,
    },
    ...(isDevMode() ? [{ value: 'api' as const, label: 'API', icon: Key }] : []),
];

const APIIntegrationsView = () => {
    const [activeTab, setActiveTab] = useState<TabId>(INTEGRATIONS_ENABLED ? 'apps' : 'integrations');
    const showTabs = TAB_ITEMS.length > 1;

    return (
        <div className="h-full flex flex-col min-w-0">
            {showTabs && (
                <div className="px-4 sm:px-6 lg:px-8 pt-4 flex-shrink-0">
                    <Tabs<TabId>
                        items={TAB_ITEMS}
                        value={activeTab}
                        onChange={setActiveTab}
                        label="Integrations sections"
                    />
                </div>
            )}

            <div className="flex-1 min-h-0 min-w-0">
                {showTabs ? (
                    <>
                        {INTEGRATIONS_ENABLED && (
                            <TabPanel value="apps" active={activeTab === 'apps'}>
                                <IntegrationsCatalog />
                            </TabPanel>
                        )}
                        <TabPanel value="integrations" active={activeTab === 'integrations'}>
                            <IntegrationDashboard />
                        </TabPanel>
                        {isDevMode() && (
                            <TabPanel value="api" active={activeTab === 'api'}>
                                <APIView />
                            </TabPanel>
                        )}
                    </>
                ) : (
                    <IntegrationDashboard />
                )}
            </div>
        </div>
    );
};

export default APIIntegrationsView;
